import { TaxBehaviour } from 'components/SalaryComponents/types';
import { format } from 'date-fns';
import { dateFormats } from 'utils/Dates';
import { paiseAmountToRupee, rupeeAmountToPaise } from 'utils/Numbers';
import {
  AssignedFlexibleComponentsTableDataType,
  DECLARATION_WINDOW_TYPES,
  DeclarationWindowSetting,
  DeductionData,
  MonthlyDeclaration,
} from './types';
import {
  BenefitsUnitTypes,
  FlexibleBenefitsComponent,
} from 'components/SalaryComponents/views/FlexibleBenefits/types';
import {
  AssignmentListItem,
  CreateAssignmentInputVariables,
} from 'api/gql-utils/flexibleBenefits/types';

export const getTaxabilityDescriptionText = (taxBehaviour?: TaxBehaviour) => {
  if (!taxBehaviour) {
    return '';
  }
  return `Tax Exemption - Old Regime: ${
    taxBehaviour.taxExemptInOldRegime ? 'Yes' : 'No'
  } | New Regime: ${taxBehaviour.taxExemptInNewRegime ? 'Yes' : 'No'}`;
};

export const getDeductionsInPaise = (deductions: DeductionData[]) => {
  return deductions.map((deduction) => ({
    componentId: deduction.componentId,
    value: rupeeAmountToPaise(deduction.monthlyAmount).toString(),
    includeInCompensation: true,
    includeInPayroll: true,
  }));
};

export const getPayslipUrl = (userId: string | number, orgId: string | number) => {
  const encodedDate = encodeURIComponent(format(new Date(), dateFormats.dateMonthYearWithSlash));
  return `/reports/paySlipNew?userId=${userId}&orgId=${orgId}&pm=${encodedDate}`;
};

const getUnitSuffix = (unit?: BenefitsUnitTypes) => {
  switch (unit) {
    case 'PERCENTAGE_OF_BASIC':
      return '% of Basic';
    case 'PERCENTAGE_OF_CTC':
      return '% of CTC';
    case 'PERCENTAGE_OF_GROSS':
      return '% of Gross';
    default:
      return '';
  }
};

export const isFBPUnitAmount = (unit?: BenefitsUnitTypes) => {
  return unit === 'ACTUAL_AMOUNT' || unit === 'UNIT_UNSPECIFIED';
};

const getDeclaredValue = (
  flexibleComponent: FlexibleBenefitsComponent,
  assignedComponent?: AssignmentListItem,
): MonthlyDeclaration => {
  const benefitsSettings = flexibleComponent.settings.flexible_settings?.benefit_amount_settings;
  const suffix = getUnitSuffix(benefitsSettings?.unit);
  const shouldConvertToRupees = isFBPUnitAmount(benefitsSettings?.unit);

  const assignedValue = assignedComponent?.assigned_value ?? null;

  if (benefitsSettings?.fixed_amount) {
    const value = assignedValue ?? benefitsSettings?.fixed_amount?.amount ?? 0;

    return {
      id: flexibleComponent.id,
      value: shouldConvertToRupees
        ? paiseAmountToRupee(Number(value))?.toString()
        : value?.toString(),
      type: 'FIXED',
      suffix: suffix,
    };
  } else if (benefitsSettings?.list_of_options_amount) {
    const default_option_index =
      benefitsSettings?.list_of_options_amount?.default_option_index ?? 0;
    const value =
      assignedValue ??
      benefitsSettings?.list_of_options_amount?.amounts?.[default_option_index] ??
      0;

    return {
      id: flexibleComponent.id,
      options:
        benefitsSettings?.list_of_options_amount?.amounts?.map((item) => {
          const optionVal = shouldConvertToRupees
            ? paiseAmountToRupee(Number(item))?.toString()
            : item?.toString();
          return {
            value: optionVal,
            label: `${optionVal}${suffix}`,
          };
        }) || [],
      value: shouldConvertToRupees
        ? paiseAmountToRupee(Number(value))?.toString()
        : value?.toString() || '',
      type: 'LIST_OF_OPTION',
      suffix: suffix,
    };
  }

  const rangeValue = assignedValue ?? benefitsSettings?.range_amount?.min ?? '0';
  return {
    id: flexibleComponent.id,
    value: shouldConvertToRupees
      ? paiseAmountToRupee(Number(rangeValue))?.toString()
      : rangeValue?.toString() || '',
    type: 'RANGE',
    suffix: suffix,
  };
};

const getMonthlyMaxValue = (flexibleComponent: FlexibleBenefitsComponent) => {
  const benefitsSettings = flexibleComponent.settings.flexible_settings?.benefit_amount_settings;
  let amount: number = 0;
  if (benefitsSettings?.fixed_amount) {
    amount = benefitsSettings?.fixed_amount?.amount || 0;
  } else if (benefitsSettings?.range_amount) {
    amount = benefitsSettings?.range_amount?.max || 0;
  } else if (benefitsSettings?.list_of_options_amount) {
    amount = Math.max(...benefitsSettings?.list_of_options_amount?.amounts);
  }

  if (isFBPUnitAmount(benefitsSettings?.unit)) return paiseAmountToRupee(amount).toString();
  return amount.toString();
};

export const getSelectionChanges = ({
  previousSelection,
  currentSelection,
}: {
  currentSelection: string[];
  previousSelection: string[];
}): {
  removedIds: string[];
  addedIds: string[];
  unchangedIds: string[];
} => {
  const removedIds = previousSelection.filter((id) => !currentSelection.includes(id));

  const addedIds = currentSelection.filter((id) => !previousSelection.includes(id));

  const unchangedIds = currentSelection.filter((id) => previousSelection.includes(id));

  return {
    removedIds,
    addedIds,
    unchangedIds,
  };
};

export const getUpdatedAssignments = ({
  currentMonthlyDeclaration,
  previousMonthlyDeclaration,
  assignedIds,
}: {
  currentMonthlyDeclaration: Record<string, string>;
  previousMonthlyDeclaration: Record<string, string>;
  assignedIds: string[];
}) => {
  const updatedAssignments: Record<string, string> = {};

  assignedIds.forEach((id) => {
    const currentValue = currentMonthlyDeclaration[id];
    const previousValue = previousMonthlyDeclaration[id];
    if (currentValue !== previousValue) {
      updatedAssignments[id] = currentValue;
    }
  });

  return updatedAssignments;
};

const getTaxableType = (flexibleComponent: FlexibleBenefitsComponent): string => {
  const tax_behaviour = flexibleComponent.settings.flexible_settings?.tax_behaviour;
  if (!tax_behaviour) {
    return 'No Regime';
  }
  if (tax_behaviour?.tax_exempt_in_old_regime && tax_behaviour?.tax_exempt_in_new_regime) {
    return 'Both Regimes';
  }
  if (tax_behaviour?.tax_exempt_in_old_regime) {
    return 'Old Regime Only';
  }
  if (tax_behaviour?.tax_exempt_in_new_regime) {
    return 'New Regime Only';
  }
  return 'No Regime';
};

export const getMergedFlexibleBenefitsData = ({
  flexibleComponents,
  assignedFlexibleComponents,
  unassignedComponents = true,
}: {
  flexibleComponents: FlexibleBenefitsComponent[];
  assignedFlexibleComponents?: AssignmentListItem[];
  unassignedComponents?: boolean;
}): AssignedFlexibleComponentsTableDataType[] => {
  const assignedComponentIdMap: Record<string, string> = {};
  let flexibleComponentsData = flexibleComponents.map((item) => {
    const assignedComponent = assignedFlexibleComponents?.find(
      (component) => component.component_id === item.id,
    );

    if (assignedComponent) {
      assignedComponentIdMap[item.id] = assignedComponent.assignment_id;
    }
    return {
      id: item.id,
      display_name: item.display_name ?? item.name,
      component_name: item.name,
      maxValuePerMonth: getMonthlyMaxValue(item),
      monthlyDeclaration: getDeclaredValue(item, assignedComponent),
      assignment_id: assignedComponent?.assignment_id,
      unit: item.settings?.flexible_settings?.benefit_amount_settings?.unit,
      canDeclare: !!item.settings?.flexible_settings?.declaration_settings?.declaration_enabled,
      taxableType: getTaxableType(item),
    };
  });

  if (!unassignedComponents)
    flexibleComponentsData = flexibleComponentsData.filter((item) => !!item.assignment_id);
  return flexibleComponentsData;
};

export const getPercentageValue = (baseValue: number, percentage: number) => {
  const percentageValue = (baseValue / 100) * percentage;
  return Math.floor(percentageValue);
};

export const isWithinTimestampRange = (startTimestamp: number, endTimestamp: number): boolean => {
  const now = Date.now();
  const startMs = startTimestamp * 1000;
  const endMs = endTimestamp * 1000;

  return now >= startMs && now <= endMs;
};

export const isWithinDayOfMonthRange = (startDay: number, endDay: number): boolean => {
  const currentDay = new Date().getDate();
  return currentDay >= startDay && currentDay <= endDay;
};

/**
 * Determines if the declaration window is currently open based on the configuration.
 *
 * @param config - The declaration window configuration from the API
 * @param currentDate - Optional date for testing (defaults to now)
 * @returns boolean indicating if the declaration window is open
 */
export const isDeclarationWindowCurrentlyOpen = (
  activeSetting: DeclarationWindowSetting,
  activeKey: string,
): boolean => {
  if (!activeSetting) {
    return false;
  }

  switch (activeKey) {
    case DECLARATION_WINDOW_TYPES.ALWAYS_OPEN:
      return !!activeSetting.boolean_value?.value;

    case DECLARATION_WINDOW_TYPES.CUSTOM_RANGE: {
      const dateRange = activeSetting.date_ranges_value?.values?.[0];
      if (!dateRange?.start || !dateRange?.end) {
        return false;
      }
      return isWithinTimestampRange(dateRange.start, dateRange.end);
    }

    default: {
      const numberRange = activeSetting.number_range_value?.value;
      if (!numberRange?.start || !numberRange?.end) {
        return false;
      }
      return isWithinDayOfMonthRange(numberRange.start, numberRange.end);
    }
  }
};
